import { ApiProblemError, throwProblem } from '@/shared/api/problem';
import { apiDelete, apiGet, apiSend, unwrapItems } from '@/shared/api/request';
import { env } from '@/shared/config/env';

/**
 * API раздела «Дизайн / Баннеры» (AUI-DSN-01).
 *
 * Типы описаны руками по контракту `docs/contracts/banners.md` (актуален на 6 сентября 2026 года):
 * снимок OpenAPI в репозитории пуст — спека админки закрыта аутентификацией, и `pnpm api:sync`
 * без токена сотрудника её не забирает. После синхронизации спеки файл должен перейти
 * на сгенерированные типы `@/shared/api/schema`.
 *
 * Баннер — только графика и ссылка. Текста, кнопок и пресетов оформления в контракте нет
 * и не будет (решение владельца от 6 сентября 2026 года): всё нужное рисуется в самой картинке,
 * витрина кладёт `<picture>` внутрь `<a>`. Единственный текст баннера — `altText`.
 */

/* =============================== Размещения =============================== */

/** Где баннер стоит на витрине. Перечень закрыт: размещения заводит вёрстка главной, а не админка. */
export type BannerPlacement = 'HERO' | 'HOME_BLOCK';

export const bannerPlacements: BannerPlacement[] = ['HERO', 'HOME_BLOCK'];

/**
 * Требования к картинкам размещения.
 *
 * Проверяет их бэкенд при создании баннера, а не при загрузке файла: медиа размещения не знает.
 * Здесь они нужны, чтобы сказать о них до загрузки — переснять кроп дешевле, чем ловить отказ
 * формы на готовом баннере.
 */
export interface PlacementSpec {
  label: string;
  where: string;
  desktop: { ratio: string; minWidth: number };
  mobile: { ratio: string; minWidth: number };
}

export const PLACEMENTS: Record<BannerPlacement, PlacementSpec> = {
  HERO: {
    label: 'Слайдер главной',
    where: 'Карусель вверху главной страницы.',
    desktop: { ratio: '3:1', minWidth: 1600 },
    mobile: { ratio: '3:2', minWidth: 960 },
  },
  HOME_BLOCK: {
    label: 'Блоки главной',
    where: 'Промо-блоки по два в ряду перед подвалом.',
    desktop: { ratio: '3:2', minWidth: 1200 },
    mobile: { ratio: '3:2', minWidth: 800 },
  },
};

/* ================================= Баннер ================================= */

/**
 * Состояние баннера на момент ответа.
 *
 * Считает его бэкенд: `SCHEDULED` и `EXPIRED` — это срок показа против текущего времени,
 * и вычислять его в браузере значило бы показывать одно, пока витрина показывает другое.
 */
export type BannerState = 'ACTIVE' | 'DISABLED' | 'SCHEDULED' | 'EXPIRED';

export const bannerStateLabels: Record<BannerState, string> = {
  ACTIVE: 'На витрине',
  DISABLED: 'Выключен',
  SCHEDULED: 'Ждёт срока',
  EXPIRED: 'Срок прошёл',
};

/**
 * Куда ведёт баннер. Весь баннер кликабелен целиком — отдельных кнопок на нём нет.
 *
 * `SECRET_PAGE` — закрытое предложение (AUI-DSN-02): именно ссылка, а не размещение, делает
 * баннер секретным. Стоять он может и в слайдере, и в блоке главной, и от соседей не отличается
 * ничем — покупатель видит обычный баннер, а ведёт тот на страницу, которой больше нигде нет.
 */
export type BannerLinkKind = 'CATEGORY' | 'PRODUCT' | 'SECRET_PAGE' | 'EXTERNAL';

export const bannerLinkKindLabels: Record<BannerLinkKind, string> = {
  CATEGORY: 'Категория',
  PRODUCT: 'Товар',
  SECRET_PAGE: 'Секретная страница',
  EXTERNAL: 'Внешний адрес',
};

/**
 * Состояние цели ссылки.
 *
 * `HIDDEN` — цель есть, но покупателю не видна (скрытая категория, товар не в продаже);
 * `MISSING` — цель удалена, и `href` тогда `null`. Оба состояния не ломают баннер в админке,
 * но на витрину он с ними не идёт: лучше пропавший слайд, чем 404 с первого экрана.
 *
 * У секретной страницы `HIDDEN` не бывает: доступностью страницы заведует сам баннер, и это
 * состояние означало бы, что он прячет себя из-за страницы, которую сам же и закрыл.
 */
export type BannerLinkStatus = 'OK' | 'HIDDEN' | 'MISSING';

export interface BannerLink {
  kind: BannerLinkKind;
  /** Идентификатор категории или товара; у внешней ссылки `null`. */
  targetId: string | null;
  /** Абсолютный адрес внешней ссылки; у внутренних `null`. */
  url: string | null;
  /** Путь внутри витрины, собранный при выдаче. `null` — цель удалена. */
  href: string | null;
  status: BannerLinkStatus;
}

/** Картинка баннера. `null` в баннере — ассет пропал из хранилища, баннер надо перезалить. */
export interface BannerImage {
  assetId: string;
  width: number;
  height: number;
  url: string;
  thumbUrl: string;
}

export interface Banner {
  id: string;
  placement: BannerPlacement;
  /** Название для списка в админке; на витрину не выдаётся. */
  name: string;
  /** Единственный текст баннера — уходит в `alt` картинки. */
  altText: string;
  desktopImage: BannerImage | null;
  mobileImage: BannerImage | null;
  link: BannerLink;
  enabled: boolean;
  sortOrder: number;
  /** Включительно. `null` — показывать с момента включения. */
  showFrom: string | null;
  /** Исключительно. `null` — показывать без конца. */
  showUntil: string | null;
  state: BannerState;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/** Тело создания и правки. У правки размещения нет: под него проверялись картинки. */
export interface BannerPayload {
  name: string;
  altText: string;
  desktopImageAssetId: string;
  mobileImageAssetId: string;
  link: { kind: BannerLinkKind; targetId?: string; url?: string };
  showFrom: string | null;
  showUntil: string | null;
}

export const BANNERS_KEY = ['banners'] as const;

export function bannersListKey(placement: BannerPlacement) {
  return [...BANNERS_KEY, placement];
}

export function bannerCardKey(id: string) {
  return [...BANNERS_KEY, 'card', id];
}

const BASE = '/v1/admin/banners';

/**
 * Список размещения по порядку показа.
 *
 * Размещение уходит параметром всегда: без него бэкенд отдаёт все баннеры сразу, а раздел
 * работает с одним размещением за раз — порядок и правила картинок у них разные.
 */
export async function fetchBanners(placement: BannerPlacement): Promise<Banner[]> {
  return unwrapItems<Banner>(await apiGet<unknown>(`${BASE}?placement=${placement}`));
}

/** Карточка баннера. Форма правки читает её сама: ссылку на баннер пересылают коллеге. */
export function fetchBanner(id: string): Promise<Banner> {
  return apiGet<Banner>(`${BASE}/${encodeURIComponent(id)}`);
}

/**
 * Создание. Новый баннер выключен и встаёт в конец размещения — включают его отдельно,
 * посмотрев, что получилось.
 */
export function createBanner(placement: BannerPlacement, payload: BannerPayload): Promise<Banner> {
  return apiSend<Banner>('POST', BASE, { placement, ...payload });
}

/** Правка целиком: полей мало, и «пустая строка очищает» здесь нечего — все они обязательны. */
export function editBanner(id: string, payload: BannerPayload): Promise<Banner> {
  return apiSend<Banner>('PUT', `${BASE}/${encodeURIComponent(id)}`, payload);
}

/** Включение — состояние, а не команда перехода: повторное включение включённого не ошибка. */
export function switchBanner(id: string, enabled: boolean): Promise<Banner> {
  return apiSend<Banner>('PATCH', `${BASE}/${encodeURIComponent(id)}/enabled`, { enabled });
}

/**
 * Порядок размещения целиком: все его баннеры по одному разу, иначе `422 order-incomplete`.
 * Ответ — список в новом порядке, `sortOrder` с единицы.
 */
export async function reorderBanners(placement: BannerPlacement, bannerIds: string[]): Promise<Banner[]> {
  return unwrapItems<Banner>(await apiSend<unknown>('PUT', `${BASE}/order`, { placement, bannerIds }));
}

/** Удаление вместе с картинками. Идемпотентно: повтор по удалённому — тот же 204. */
export function deleteBanner(id: string): Promise<void> {
  return apiDelete(`${BASE}/${encodeURIComponent(id)}`);
}

/* ============================= Картинки баннера ============================= */

/** Ассет из медиа. `usage` возвращается, чтобы отличить снимок товара от картинки баннера. */
export interface BannerAsset {
  assetId: string;
  usage: string;
  width: number;
  height: number;
  urls: { original: string; card: string; thumb: string };
}

/** Контракт файла — тот же, что у снимков товара; пределы размеров у баннера свои. */
export const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png'];
export const BANNER_MIN_WIDTH_PX = 800;
export const BANNER_MIN_HEIGHT_PX = 320;

/** Потолки текстовых полей из контракта: длиннее — `422 banner-invalid`. */
export const NAME_MAX = 120;
export const ALT_TEXT_MAX = 200;
export const LINK_URL_MAX = 500;

/**
 * Отказы загрузки файла. Приходят как `422 image-invalid` с кодом в `errors[]` и относятся
 * к одной картинке, а не ко всей форме: вторая картинка и поля целы.
 */
const UPLOAD_ERRORS: Record<string, string> = {
  'image-too-large': 'Файл больше 5 МБ',
  'image-too-small': `Картинка меньше ${BANNER_MIN_WIDTH_PX}×${BANNER_MIN_HEIGHT_PX} пикселей`,
  'image-unsupported-type': 'Подходят только JPG и PNG',
  'image-unreadable': 'Файл повреждён или это не изображение',
  'image-too-many-pixels': 'Разрешение больше 50 Мп',
  'file-too-large': 'Файл больше 5 МБ',
};

/**
 * Нарушения баннера (`422 banner-invalid`).
 *
 * Соотношение и ширину проверяет не загрузка, а создание баннера: медиа не знает размещения.
 * Поэтому отказ приходит на готовую форму, и текст обязан объяснить, что не так с картинкой,
 * а не просто сказать «неверный формат».
 */
export const BANNER_ERRORS: Record<string, string> = {
  'image-not-found': 'Картинки нет в хранилище — загрузите заново',
  'image-not-banner': 'Файл загружался не как баннер — загрузите его в это поле заново',
  'image-aspect-mismatch': 'Соотношение сторон не подходит размещению',
  'image-too-narrow': 'Соотношение верное, но ширина меньше минимальной',
  'image-already-attached': 'Эта картинка уже стоит в другом баннере',
  'images-must-differ': 'Десктоп и мобильный не могут быть одной картинкой',
  'name-required': 'Название обязательно',
  'name-too-long': `Название не длиннее ${NAME_MAX} символов`,
  'alt-text-required': 'Описание картинки обязательно',
  'alt-text-too-long': `Описание не длиннее ${ALT_TEXT_MAX} символов`,
  'link-target-required': 'Выберите, куда ведёт баннер',
  'link-target-not-found': 'Цель удалена — выберите другую',
  'link-target-already-used': 'На эту секретную страницу уже ведёт другой баннер — у страницы он ровно один',
  'link-url-required': 'Адрес обязателен',
  'link-url-invalid': 'Нужен абсолютный адрес, начинающийся с https://',
  'link-url-too-long': `Адрес не длиннее ${LINK_URL_MAX} символов`,
  'schedule-inverted': 'Конец показа раньше начала',
  'order-incomplete': 'Порядок не покрывает размещение — обновите страницу',
};

/**
 * Проверка до отправки: размер и тип видно в браузере, и гонять пятимегабайтный файл ради
 * предсказуемого отказа незачем. Размеры сторон проверяет сервер — он всё равно открывает файл.
 */
export function checkImageFile(file: File): string | null {
  if (file.size > IMAGE_MAX_BYTES) {
    return UPLOAD_ERRORS['image-too-large'] ?? null;
  }
  // Тип берётся из содержимого, а не из расширения, — так же поступает и бэкенд. Пустой
  // `type` (файл из архива, перетаскивание из некоторых приложений) отправляем: разберётся сервер.
  if (file.type && !IMAGE_MIME_TYPES.includes(file.type)) {
    return UPLOAD_ERRORS['image-unsupported-type'] ?? null;
  }
  return null;
}

/** Отказ загрузки — в текст для поля картинки. Код лежит в `errors[]`, а не в `type`. */
export function describeUploadError(error: unknown): string {
  if (!(error instanceof ApiProblemError)) {
    return 'Файл не дошёл до сервера — попробуйте отправить снова';
  }
  const code = error.problem.errors?.[0]?.code;
  if (code && UPLOAD_ERRORS[code]) {
    return UPLOAD_ERRORS[code] ?? '';
  }
  if (UPLOAD_ERRORS[error.problemType]) {
    return UPLOAD_ERRORS[error.problemType] ?? '';
  }
  if (error.status === 401) {
    return 'Сессия истекла — войдите и отправьте файл снова';
  }
  return error.problem.detail ?? 'Картинку не приняли — попробуйте другой файл';
}

/**
 * Загрузка картинки баннера.
 *
 * Уходит в медиа сразу, как человек выбрал файл, — баннера для этого не нужно: форма копит
 * `assetId` и отправляет их в теле создания. Назначение `BANNER` меняет пределы размеров:
 * снимок товара требует короткую сторону от 1000 пикселей, баннеру этого не выдержать.
 *
 * Ассет принадлежит ровно одному баннеру: заменённая картинка удаляется из хранилища вместе
 * со всеми вариантами, а попытка поставить чужую даёт `image-already-attached`.
 */
export async function uploadBannerImage(file: File): Promise<BannerAsset> {
  const form = new FormData();
  form.append('file', file);
  // Content-Type не ставим намеренно: границу multipart проставляет браузер, и заданный
  // руками заголовок оставил бы тело без неё — сервер ответил бы 415.
  const response = await fetch(`${env.apiBaseUrl}/v1/admin/media/images?usage=BANNER`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!response.ok) {
    await throwProblem(response);
  }
  return (await response.json()) as BannerAsset;
}

/* ================================= Подписи ================================= */

/**
 * Куда ведёт баннер — строкой для списка.
 *
 * У внутренней ссылки показывается путь витрины, а не название цели: названия в ответе нет,
 * а путь — то самое, куда покупатель попадёт. Удалённая цель пути не имеет вовсе.
 */
export function linkSummary(link: BannerLink): string {
  if (link.status === 'MISSING') {
    return 'цель удалена';
  }
  return link.href ?? link.url ?? '—';
}

/** Срок показа строкой: «с 1 сентября», «до 1 октября», «1–30 сентября» или пусто. */
export function scheduleSummary(banner: Banner, format: (iso: string) => string): string | null {
  if (banner.showFrom === null && banner.showUntil === null) {
    return null;
  }
  if (banner.showFrom !== null && banner.showUntil !== null) {
    return `${format(banner.showFrom)} — ${format(banner.showUntil)}`;
  }
  return banner.showFrom !== null ? `с ${format(banner.showFrom)}` : `до ${format(banner.showUntil ?? '')}`;
}
